import { useState } from 'react';
import { IonDatetime, IonModal } from '@ionic/react';
import { cn, haptic, toDateKeyFromISO } from '@shared/utils';
import {
  SHEET_BREAKPOINTS,
  SHEET_INITIAL_BREAKPOINT,
  sheetClass,
} from './sheet';

export interface DatePickerSheetProps {
  isOpen: boolean;
  /** 目前選取的日期（'YYYY-MM-DD'） */
  dateKey: string;
  /** 要在月曆上標色的日子（例如有紀錄的那幾天），讓月曆同時是一份概覽 */
  highlightedDates?: ReadonlySet<string>;
  onSelect: (dateKey: string) => void;
  onClose: () => void;
}

/**
 * 標色日子的樣式。
 *
 * `IonDatetime` 的 `highlightedDates` 只收顏色字串，會被寫成 shadow DOM 內部元素的
 * inline style。這裡直接給 `var(--color-*)`——CSS 自訂屬性會繼承穿過 shadow 邊界，
 * 因此仍是走設計 token，不必在 JS 端鏡射色值字面量。
 */
const HIGHLIGHTED_DATE_STYLE = {
  textColor: 'var(--color-accent)',
  backgroundColor: 'var(--color-accent-muted)',
} as const;

/**
 * 底部面板裡的月曆，選了日期就關閉。
 *
 * 用 `IonDatetime` 而非原生 `<input type="date">`：後者的彈出層由瀏覽器繪製，
 * 樣式與主題不受控。
 */
export function DatePickerSheet({
  isOpen,
  dateKey,
  highlightedDates,
  onSelect,
  onClose,
}: DatePickerSheetProps) {
  /*
   * 月曆要等面板**完全展開後**才掛載。
   *
   * IonModal 在 `willPresent` 時掛載子元素，那時面板還沒有尺寸。`ion-datetime`
   * 以 IntersectionObserver 判定可見後才顯示日期格，對零面積目標建立的觀察在部分
   * WebView 不會再觸發，日期格會間歇性地整片空白（反覆開關面板最容易重現）。
   *
   * 等 `didPresent` 後才掛載即可避免。面板高度固定，內容晚一拍出現不會讓版面跳動。
   */
  const [isPresented, setIsPresented] = useState(false);

  return (
    <IonModal
      isOpen={isOpen}
      onDidPresent={() => setIsPresented(true)}
      onDidDismiss={() => {
        /* 卸載月曆，下次開啟才會是全新的實例（IonModal 自己也會卸載子元素，
           但這個狀態必須自行歸位，否則下一輪會停在 true） */
        setIsPresented(false);
        onClose();
      }}
      breakpoints={SHEET_BREAKPOINTS}
      initialBreakpoint={SHEET_INITIAL_BREAKPOINT}
      handle
      /* 月曆本身高度固定（六週 + 星期列 + 月份切換），面板只需容納它與把手 */
      className={sheetClass('[--height:30rem]')}
    >
      {/*
        每次開啟都以最新的 `dateKey` 為準，不需要額外重置。日曆的配色來自
        `--ion-color-*` 與階調變數，已由 `@mobile/theme` 對應到設計 token，
        這裡只需處理版面。
      */}
      {isPresented ? (
        <IonDatetime
          presentation="date"
          locale="zh-TW"
          value={dateKey}
          /* Ionic 傳進來的是 ISO 字串，統一過一次轉換才不必假設它的精確格式 */
          highlightedDates={(iso) =>
            highlightedDates?.has(toDateKeyFromISO(iso) ?? iso)
              ? HIGHLIGHTED_DATE_STYLE
              : undefined
          }
          onIonChange={(event) => {
            const value = event.detail.value;
            /* 只處理單日選取；`multiple` 模式的陣列在這裡不會出現，出現也不該猜 */
            if (typeof value !== 'string') return;

            const nextDateKey = toDateKeyFromISO(value);
            if (nextDateKey === null) return;

            haptic('tap');
            onSelect(nextDateKey);
            onClose();
          }}
          className={cn(
            /* 月曆寬度固定 350px，置中才不會在寬螢幕上黏著左緣 */
            'mx-auto [--background:transparent]',
            /*
            讓出拖曳把手的空間。把手由 IonModal 絕對定位在頂端且不佔版面高度，
            月曆不自行留白就會直接貼上去。1.5rem 與 SHEET_TOOLBAR_CLASS 的
            `--padding-top` 同值——面板頂端與內容的距離，全 app 只該有一個答案。
          */
            'mt-6',
          )}
        />
      ) : null}
    </IonModal>
  );
}
